/* eslint-disable react/display-name */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Flex, Spacer, Textarea, Button, useToast } from '@chakra-ui/react';
import FormInput from '../FormInput';
import { IFormInputs } from './interfaces';
import useContactFormValidation from './utils/useContactFormValidation';
import ContactFormMessage from './ContactFormMessage';
import sendContactMail from '@src/lib/networking/mail-api';
import { Loader } from '@components/Shared/Loader';
import { colorsGrey } from '@src/theme/chakraTheme/colors';

const ContactMeForm: React.FC = () => {
  const validationSchema = useContactFormValidation();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormInputs>({
    resolver: yupResolver(validationSchema),
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = async ({ name, email, message }: IFormInputs) => {
    setLoading(true);
    const { status } = await sendContactMail(name, email, message);
    setLoading(false);
    if (status < 300) {
      reset();
      toast({
        status: 'success',
        duration: 2000,
        position: 'bottom',
        render: () => (
          <ContactFormMessage
            title="Thank you"
            description="I'll get back to you soon"
          />
        ),
      });
    } else
      toast({
        status: 'error',
        duration: 2000,
        position: 'bottom',
        render: () => (
          <ContactFormMessage
            title="Error"
            description="An error has occured"
          />
        ),
      });
  };

  if (loading) return <Loader />;

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      w="100%"
      direction="column"
    >
      <Flex mb={4}>
        <FormInput
          placeholder="Name"
          fieldError={errors.name}
          formControlProps={{
            w: '47%',
            backgroundColor: 'white',
            border: '1px',
            borderColor: 'grey.100',
          }}
          register={register('name')}
        />
        <Spacer />
        <FormInput
          placeholder="Email"
          fieldError={errors.email}
          formControlProps={{
            w: '47%',
            backgroundColor: 'white',
            border: '1px',
            borderColor: 'grey.100',
          }}
          register={register('email')}
        />
      </Flex>
      <Textarea
        placeholder="Message"
        resize="none"
        fontFamily="montserrat"
        fontWeight="light"
        color={colorsGrey[300]}
        bgColor="white"
        borderColor="gray.300"
        borderRadius="0px"
        minH="134px"
        mb={4}
        _focusVisible={{
          outline: 'none',
        }}
        _focus={{
          outline: 'none',
        }}
        _placeholder={{ color: colorsGrey[200] }}
        {...register('message')}
      />
      <Button
        alignSelf={['center', null, 'flex-start']}
        type="submit"
        color="white"
        bgColor="primary"
        fontFamily="montserrat"
        fontWeight="semibold"
        outline="none"
        py={2}
        px={4}
        border="1px"
        borderStyle="solid"
        borderColor="primary"
        borderRadius="0px"
        transition="all 0.3s ease"
        margin="0 auto"
        _hover={{
          color: 'primary',
          bgColor: 'white',
        }}
      >
        SUBMIT
      </Button>
    </Flex>
  );
};

export default ContactMeForm;
